import React, { useState, useEffect } from 'react';
import {
  FiBook, FiUsers, FiRefreshCw, FiAlertCircle,
  FiArrowUpRight, FiArrowDownRight,
} from 'react-icons/fi';
import Card from '../../components/Card/Card';
import Badge from '../../components/Badge/Badge';
import api from '../../api';
import './Dashboard.css';

const Dashboard = ({ onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/stats.php')
      .then(res => {
        setStats(res.data);
        setLoading(false);
      })
      .catch(err => console.error(err));
  }, []);

  if (loading || !stats) return <div className="loading-state">Loading dashboard...</div>;

  const cards = [
    { id: 'total-books', label: 'Total Books', value: stats.totalBooks, icon: <FiBook />, color: 'primary' },
    { id: 'active-members', label: 'Total Members', value: stats.totalMembers, icon: <FiUsers />, color: 'success' },
    { id: 'books-issued', label: 'Books Issued', value: stats.totalIssued, icon: <FiRefreshCw />, color: 'warning' },
    { id: 'overdue', label: 'Overdue Returns', value: stats.totalOverdue, icon: <FiAlertCircle />, color: 'danger' },
  ];

  const availablePct = stats.totalBooks > 0 ? (((stats.totalBooks - stats.totalIssued) / stats.totalBooks) * 100).toFixed(1) : 0;
  const issuedPct = stats.totalBooks > 0 ? ((stats.totalIssued / stats.totalBooks) * 100).toFixed(1) : 0;

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h2 className="page-header__title">Welcome back, Admin 👋</h2>
          <p className="page-header__sub">Real-time status of your library ecosystem</p>
        </div>
        <div className="page-header__date">
          <span>{new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</span>
        </div>
      </div>

      <div className="dashboard__stats">
        {cards.map((stat) => (
          <Card key={stat.id} className={`stat-card stat-card--${stat.color}`} padding="md" hoverable onClick={() => onNavigate?.(stat.id.split('-')[1])}>
            <div className="stat-card__layout">
              <div>
                <p className="stat-card__label">{stat.label}</p>
                <p className="stat-card__value">{stat.value?.toLocaleString()}</p>
              </div>
              <div className={`stat-card__icon-wrap stat-card__icon-wrap--${stat.color}`}>
                {stat.icon}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="dashboard__grid">
        {/* Recent Transactions */}
        <Card className="dashboard__panel" padding="none">
          <div className="panel-header">
            <div>
              <h3 className="panel-header__title">Recent Transactions</h3>
              <p className="panel-header__sub">Latest issue and return activity</p>
            </div>
            <button className="panel-header__link" onClick={() => onNavigate?.('issue-return')}>View all</button>
          </div>

          <div className="transaction-list">
            {stats.recentIssues?.length > 0 ? stats.recentIssues.map((tx) => (
              <div key={tx.IssueId} className="transaction-item">
                <div className="transaction-item__avatar">{tx.MemberName?.charAt(0)}</div>
                <div className="transaction-item__info">
                  <p className="transaction-item__member">{tx.MemberName}</p>
                  <p className="transaction-item__book">{tx.BookTitle}</p>
                </div>
                <div className="transaction-item__right">
                  <Badge variant={tx.Status === 'Returned' ? 'success' : (tx.Status === 'Overdue' ? 'danger' : 'warning')} dot>
                    {tx.Status}
                  </Badge>
                  <p className="transaction-item__date">{new Date(tx.IssueDate).toLocaleDateString('en-IN', {day:'2-digit', month:'short'})}</p>
                </div>
              </div>
            )) : <p style={{padding:20, color:'var(--text-muted)'}}>No recent activity found.</p>}
          </div>
        </Card>

        {/* Quick Stats Overlay */}
        <div className="dashboard__side">
          <Card padding="md">
            <Card.Header>
              <Card.Title>Inventory Overview</Card.Title>
            </Card.Header>
            <div className="quick-stats">
              {[
                { label: 'Available Books', value: (stats.totalBooks - stats.totalIssued), percent: availablePct, color: '#10b981' },
                { label: 'Issued Ratio',    value: stats.totalIssued,   percent: issuedPct, color: '#f59e0b' },
                { label: 'Overdue Books',   value: stats.totalOverdue,  percent: ((stats.totalOverdue / (stats.totalIssued || 1)) * 100).toFixed(1), color: '#ef4444' },
              ].map((item) => (
                <div key={item.label} className="quick-stat-item">
                  <div className="quick-stat-item__top">
                    <span className="quick-stat-item__label">{item.label}</span>
                    <span className="quick-stat-item__value">{item.value?.toLocaleString()}</span>
                  </div>
                  <div className="quick-stat-item__bar-bg">
                    <div
                      className="quick-stat-item__bar-fill"
                      style={{ width: `${Math.min(item.percent, 100)}%`, backgroundColor: item.color }}
                    />
                  </div>
                  <span className="quick-stat-item__pct">{item.percent}%</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
